import { useRef } from 'react'

import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, ease: [0.25, 0, 0.1, 1], delay }}
    >
      {children}
    </motion.div>
  )
}

function ImgSlot({ label, className = '' }: { label: string; className?: string }) {
  return (
    <div className={`bg-cream-200 flex items-center justify-center ${className}`}>
      <p className="text-[10px] tracking-widest2 uppercase text-ink-light">{label}</p>
    </div>
  )
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const TREEHOUSES = [
  {
    id: 'canopy-nest',
    label: 'The Canopy Nest',
    type: 'Single-Storey Treehouse',
    price: '₹8,500',
    baseOccupancy: '2 adults',
    maxOccupancy: '3 adults (or 2 adults + 2 children under 10)',
    guestProfile: 'Couples · Honeymooners · Solo Writers',
    desc: 'A private treehouse perched in the oak canopy with uninterrupted views across the Himalayan valley. A personal deck faces east for sunrise. Inside: a king bed, warm timber walls, and the kind of quiet that is hard to find elsewhere.',
    highlights: ['Private deck', 'Valley views', 'King bed', 'Wood-fire heating'],
    imgLabels: ['Canopy Nest Exterior', 'Canopy Nest Interior', 'Canopy Nest Deck'],
    imgs: [
      { src: '/images/optimized/IMG_5163.webp', alt: 'Canopy Nest' },
      { src: '/images/optimized/IMG_5160.webp', alt: 'Canopy Nest Interior' },
      { src: '/images/optimized/IMG_5168.webp', alt: 'Canopy Nest Exterior' },
    ],
  },
  {
    id: 'canopy-loft',
    label: 'The Canopy Loft',
    type: 'Duplex Family Treehouse',
    price: '₹13,500',
    baseOccupancy: '4 adults',
    maxOccupancy: '6 adults (extra bedding on mezzanine floor)',
    guestProfile: 'Families · Small Groups · Co-workers',
    desc: 'A two-storey treehouse suite built on the ridge with panoramic views on both sides. The lower floor has the main bedroom and living area; the mezzanine sleeps additional guests. Ideal for families or groups wanting space without sacrificing the forest feeling.',
    highlights: ['Two levels', 'Mezzanine sleeping area', 'Ridge-top panoramas', 'Seats 4-6'],
    imgLabels: ['The Canopy Loft Exterior', 'The Canopy Loft Interior', 'The Canopy Loft View'],
    imgs: [
      { src: '/images/optimized/IMG_5113.webp', alt: 'The Canopy Loft' },
      { src: '/images/optimized/img_famTreeHouse1.webp', alt: 'The Canopy Loft Interior' },
      { src: '/images/optimized/img_famTreeHouse2.webp', alt: 'The Canopy Loft View' },
    ],
  },
]

const FAMILY_SUITE = {
  id: 'family-duplex-suite',
  label: 'Family Duplex Suite',
  type: 'Family Duplex Suite',
  price: '₹13,500',
  baseOccupancy: '4 adults',
  maxOccupancy: '6 adults (extra bedding on mezzanine floor)',
  guestProfile: 'Families · Small Groups · Co-workers',
  desc: 'The largest cabin at Scenic Solitude. A two-storey duplex lodge with a mezzanine that comfortably accommodates up to six. A generous living area, a wood-burning stove, and a long verandah that invites you to stay past checkout.',
  highlights: ['Two levels', 'Mezzanine floor', 'Wood-burning stove', 'Large verandah', 'Seats 4-6'],
  imgLabels: ['Family Suite Exterior', 'Family Suite Interior', 'Family Suite Mezzanine'],
  imgs: [
    { src: '/images/optimized/Img_familySuite.webp', alt: 'Family Duplex Suite Room' },
    { src: '/images/optimized/img_familysuite2.webp', alt: 'Family Duplex Suite Interior' },
    { src: '/images/optimized/img_familysuite1.webp', alt: 'Family Duplex Suite' },
  ],
}

const CABINS = [
  {
    id: 'wooden-cabins',
    label: 'Wooden Cabins 1 to 5',
    type: 'Wooden Cabin',
    price: '₹11,500',
    baseOccupancy: '2 adults per cabin',
    maxOccupancy: '3 adults per cabin (or 2 adults + 2 children under 10)',
    guestProfile: 'Couples · Honeymooners · Solo Writers',
    desc: 'Five identical handcrafted cabins set quietly on the hillside, each built from local timber with its own private entrance and verandah. Warm in winter, cool in summer, and close to the earth in all seasons. Book one for a private retreat or take several for a group stay among friends or colleagues.',
    highlights: ['Five cabins', 'Queen bed', 'Private verandah', 'Forest setting', 'Group booking option'],
    imgLabels: ['Cabin Exterior', 'Cabin Interior'],
    imgs: [
      { src: '/images/optimized/img_cab1.webp', alt: 'Wooden Cabin' },
      { src: '/images/optimized/img_cab2.webp', alt: 'Wooden Cabin Interior' },
      { src: '/images/optimized/img_cab3.webp', alt: 'Wooden Cabin View' },
    ],
  },
]

// ─── Room Card ────────────────────────────────────────────────────────────────

interface Room {
  id: string
  label: string
  type: string
  price: string
  baseOccupancy: string
  maxOccupancy: string
  guestProfile: string
  desc: string
  highlights: string[]
  imgLabels: string[]
  imgs?: { src: string; alt: string }[]
}

function RoomCard({ room, index, wide = false }: { room: Room; index: number; wide?: boolean }) {
  return (
    <FadeIn delay={index * 0.1} className="border-t border-cream-300 pt-12 pb-16">
      <div className={`grid gap-10 ${wide ? 'lg:grid-cols-[1fr_1.2fr]' : 'lg:grid-cols-2'} lg:items-start`}>

        {/* Images */}
        {room.imgs ? (
          <div className={`grid gap-1 ${room.imgs.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <img src={room.imgs[0].src} alt={room.imgs[0].alt} loading="lazy"
              className={`${room.imgs.length > 1 ? 'col-span-2' : ''} aspect-[4/3] w-full object-cover`} />
            {room.imgs.slice(1).map(img => (
              <img key={img.src} src={img.src} alt={img.alt} loading="lazy"
                className="aspect-square w-full object-cover" />
            ))}
          </div>
        ) : (
          <div className={`grid gap-1 ${room.imgLabels.length >= 3 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {room.imgLabels[0] && (
              <ImgSlot
                label={room.imgLabels[0]}
                className={`aspect-[4/3] w-full ${room.imgLabels.length >= 3 ? 'col-span-2' : ''}`}
              />
            )}
            {room.imgLabels.slice(1).map(lbl => (
              <ImgSlot key={lbl} label={lbl} className="aspect-square w-full" />
            ))}
          </div>
        )}

        {/* Details */}
        <div>
          <p className="text-[10px] tracking-widest2 uppercase text-ink-muted mb-3">{room.type}</p>
          <div className="flex items-baseline justify-between gap-6">
            <h2 className="text-3xl font-semibold text-ink">{room.label}</h2>
            <div className="text-right shrink-0">
              <p className="text-xl font-semibold text-ink">{room.price}</p>
              <p className="text-[10px] tracking-widest text-ink-light uppercase">per night</p>
            </div>
          </div>

          <p className="mt-6 text-sm font-light text-ink-soft leading-[1.9]">{room.desc}</p>

          <div className="mt-8 flex flex-wrap gap-2">
            {room.highlights.map(h => (
              <span key={h} className="border border-cream-400 px-3 py-1 text-[10px] tracking-widest uppercase text-ink-muted">
                {h}
              </span>
            ))}
          </div>

          <div className="mt-8 space-y-3 border-t border-cream-300 pt-8">
            <div className="flex gap-8">
              <p className="text-[10px] tracking-widest2 uppercase text-ink-light w-28 shrink-0">Base Rate</p>
              <p className="text-sm font-light text-ink-soft">{room.baseOccupancy}</p>
            </div>
            <div className="flex gap-8">
              <p className="text-[10px] tracking-widest2 uppercase text-ink-light w-28 shrink-0">Max Capacity</p>
              <p className="text-sm font-light text-ink-soft">{room.maxOccupancy}</p>
            </div>
            <div className="flex gap-8">
              <p className="text-[10px] tracking-widest2 uppercase text-ink-light w-28 shrink-0">Ideal For</p>
              <p className="text-sm font-light text-ink-soft">{room.guestProfile}</p>
            </div>
          </div>

          <Link
            to={`/inquire?cabin=${room.id}`}
            className="mt-10 btn-outline-aman text-ink inline-flex"
          >
            Enquire About This Room
          </Link>
        </div>

      </div>
    </FadeIn>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Treehouses() {
  return (
    <div className="pb-24 lg:pb-32">

      {/* ── Hero banner ── */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1e3525', minHeight: '380px' }}>
        {/* background image at low opacity */}
        <img
          src="/images/optimized/IMG_5169.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        {/* radial vignette */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 100% at 50% 0%, transparent 40%, rgba(0,0,0,0.45) 100%)' }} />

        <div className="relative z-10 wrap py-24 lg:py-32">
          <FadeIn>
            <span className="text-[10px] tracking-[0.35em] uppercase text-white/50 font-semibold mb-5 block">Accommodations</span>
            <h1 className="text-5xl lg:text-7xl font-semibold text-white leading-tight tracking-tight">
              Treehouses<br />&amp; Wooden Cabins
            </h1>
            <p className="mt-6 text-white/65 font-light leading-[1.9] max-w-lg">
              Eight accommodations in total: two treehouses, one family duplex suite, and five wooden cabins.
              Each hand-built and positioned for privacy in the forest above Sojha. All rates include breakfast.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* ── Pricing strip ── */}
      <FadeIn>
        <div className="wrap">
          <div className="grid grid-cols-2 md:grid-cols-4 -mt-1 shadow-lg">
            {[
              { label: 'The Canopy Nest', sub: 'Treehouse', price: '₹8,500' },
              { label: 'The Canopy Loft', sub: 'Family Treehouse', price: '₹13,500' },
              { label: 'Family Duplex Suite', sub: 'Duplex Suite', price: '₹13,500' },
              { label: 'Wooden Cabins 1-5', sub: 'Per cabin', price: '₹11,500' },
            ].map((item, i) => (
              <div key={item.label} className={`px-6 py-7 border-b border-r border-cream-300 ${i === 0 ? 'border-l' : ''} bg-cream-100`}>
                <p className="text-[10px] tracking-widest2 uppercase text-ink-muted mb-2">{item.sub}</p>
                <p className="font-semibold text-sm text-ink">{item.label}</p>
                <p className="mt-3 text-2xl font-semibold text-ink">{item.price}</p>
                <p className="text-[10px] tracking-widest text-ink-light uppercase">/ night</p>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      <div className="wrap mt-20 mb-0">

        {/* Treehouses */}
        <FadeIn>
          <p className="label-aman mb-2">Treehouses</p>
          <p className="text-sm font-light text-ink-soft mb-12 max-w-lg">
            Built into the oak canopy. No two nights feel the same.
          </p>
        </FadeIn>

        {TREEHOUSES.map((room, i) => (
          <RoomCard key={room.id} room={room} index={i} wide />
        ))}

        {/* Family Duplex Suite */}
        <FadeIn className="mt-20">
          <p className="label-aman mb-2">Family Duplex Suite</p>
          <p className="text-sm font-light text-ink-soft mb-12 max-w-lg">
            Our largest accommodation. Ideal for families and small groups wanting generous space in the forest.
          </p>
        </FadeIn>

        <RoomCard room={FAMILY_SUITE} index={0} wide />

        {/* Wooden Cabins */}
        <FadeIn className="mt-20">
          <p className="label-aman mb-2">Wooden Cabins</p>
          <p className="text-sm font-light text-ink-soft mb-12 max-w-lg">
            Handcrafted from local timber, set quietly on the hillside. Warm in winter, cool in summer.
          </p>
        </FadeIn>

        {CABINS.map((room, i) => (
          <RoomCard key={room.id} room={room} index={i} />
        ))}

      </div>{/* end wrap */}

      {/* CTA */}
      <FadeIn className="mt-24">
        <div className="relative overflow-hidden py-24 px-8 text-center" style={{ backgroundColor: '#1e3525' }}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #a3c47a 0%, transparent 60%), radial-gradient(circle at 80% 30%, #4a7c59 0%, transparent 50%)' }} />
          <div className="relative z-10">
            <p className="text-[10px] tracking-[0.35em] uppercase text-white/50 font-semibold mb-4 block">Ready to Visit?</p>
            <h2 className="text-4xl font-semibold text-white mb-6">Reserve Your Stay</h2>
            <p className="text-sm font-light text-white/70 max-w-md mx-auto mb-10 leading-relaxed">
              Enquire and we will respond within 24 hours to confirm availability and discuss your visit.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link to="/inquire" className="btn-ghost-aman">Send an Enquiry</Link>
              <Link to="/contact" className="text-[11px] tracking-widest2 uppercase text-white/50 hover:text-white transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </FadeIn>

    </div>
  )
}
